import { Box, Container, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination as MuiPagination, TableRow, TableFooter } from '@mui/material';
import { TableCellProps } from '@mui/material';
import { IPagingData } from '@sr/dto';
import { ElementType, ReactNode, useRef, useState, useEffect } from 'react';
import { ErrorBanner } from './error-banner';
import { LoadingBanner } from './loading-banner';

export const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
export const DEFAULT_ITEMS_PER_PAGE = 25;
export const DEFAULT_PAGINATION: IPagingData = {
  page: 0,
  itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
};

export type HeadCell = {
  label: ReactNode;
  align?: TableCellProps['align'];
  padding?: TableCellProps['padding'];
  color?: string;
  additionalIcon?: ReactNode;
};

export type HeadCellsCollection = Array<string | HeadCell>;

export type Pagination = {
  page: number;
  onChangePage: (page: number) => void;
  itemsPerPage: number;
  onChangeItemsPerPage: (value: number) => void;
  totalItemsCount: number;
};

type Props<T> = {
  tableOptions?: {
    component: ElementType<any>;
  };
  columns: HeadCellsCollection;
  items: Array<T>;
  isLoading: boolean;
  error?: string;
  itemRender: (arg: T, index: number) => ReactNode;
  newItemRowRender?: () => ReactNode;
  showNewItemRow?: boolean;
  noItemsText: string;
  footer?: (items: Array<T>) => ReactNode;
  pagingData?: IPagingData;
  totalItemsCount?: number;
  onChangePaging?: (paging: IPagingData) => void;
  itemsPerPageOptions?: number[];
  renderCustomHeader?: () => ReactNode;
  renderTableHeader?: () => ReactNode;
  height?: number | string;
  flex?: number;
  minHeight?: number;
};

export function GridLayout<T>(props: Props<T>) {
  const {
    items,
    isLoading,
    error,
    itemRender,
    newItemRowRender,
    noItemsText,
    columns,
    showNewItemRow,
    pagingData,
    onChangePaging,
    totalItemsCount,
    tableOptions,
    footer,
    itemsPerPageOptions = ITEMS_PER_PAGE_OPTIONS,
    renderCustomHeader,
    renderTableHeader,
    height,
    flex,
    minHeight = 250,
  } = props;

  const component = tableOptions?.component || Paper;
  const containerRef = useRef<HTMLDivElement>(null);
  const [calculatedHeight, setCalculatedHeight] = useState<number>(0);

  const showNewItem = showNewItemRow || newItemRowRender !== undefined;

  useEffect(() => {
    if (height || flex) return;

    const calculateHeight = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const topPosition = rect.top;
        const available = viewportHeight - topPosition - 20;
        setCalculatedHeight(Math.max(available, minHeight));
      }
    };

    calculateHeight();
    window.addEventListener('resize', calculateHeight);
    
    return () => {
      window.removeEventListener('resize', calculateHeight);
    };
  }, [height, flex, minHeight]);

  const containerStyle = flex ? {
    flex: flex,
    minHeight: minHeight,
    display: 'flex',
    flexDirection: 'column' as const,
  } : {};

  const finalHeight = height || (flex ? 'auto' : (calculatedHeight > 0 ? calculatedHeight : 'auto'));

  if (error)
    return (
      <Container>
        <ErrorBanner errorMessage={error} />
      </Container>
    );
    
  return (
    <Box 
      ref={containerRef} 
      sx={{ 
        width: '100%', 
        height: '100%',
        ...containerStyle 
      }}
    >
      {renderCustomHeader && renderCustomHeader()}
      
      <TableContainer
        component={component}
        sx={{
          width: '100%',
          overflow: 'auto',
          maxHeight: finalHeight,
          ...(flex && { flex: 1 }),
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            width: '100%',
            display: 'table',
          }}
        >
          {renderTableHeader ? renderTableHeader() : <TableHead>
            <TableRow sx={{ zIndex: 1000 }}>
              {columns.map((header, idx) =>
                typeof header === 'string' ? (
                  <TableCell key={idx}>{header}</TableCell>
                ) : (
                  <TableCell
                    key={idx}
                    align={header.align || 'right'}
                    padding={header.padding}
                    sx={{
                      backgroundColor: header.color || 'white',
                      width: 'auto',
                    }}
                  >
                    {header.label}
                    {header.additionalIcon ?? null}
                  </TableCell>
                ),
              )}
            </TableRow>
          </TableHead>}

          <TableBody>
            {items.length === 0 ? (
              isLoading ? (
                <FullSpanRow columnsCount={columns.length} content={<LoadingBanner />} />
              ) : (
                <FullSpanRow columnsCount={columns.length} content={noItemsText} />
              )
            ) : (
              items.map(itemRender)
            )}
            {showNewItem && newItemRowRender && newItemRowRender()}
          </TableBody>

          {(footer || (pagingData && onChangePaging && totalItemsCount !== undefined)) && (
            <TableFooter
              sx={{
                position: 'sticky',
                bottom: 0,
                backgroundColor: 'background.paper',
                zIndex: 1000,
              }}
            >
              {footer && footer(items)}

              {pagingData && onChangePaging && totalItemsCount !== undefined && (
                <TableRow>
                  <TableCell colSpan={columns.length} sx={{ maxHeight: '40px' }}>
                    <TablePagination paging={pagingData} onChange={onChangePaging} totalItemsCount={totalItemsCount} itemsPerPageOptions={itemsPerPageOptions} />
                  </TableCell>
                </TableRow>
              )}
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </Box>
  );
}

const FullSpanRow = ({ columnsCount, content }: { columnsCount: number; content: ReactNode }) => {
  return (
    <TableRow>
      <TableCell colSpan={columnsCount}>
        <Box sx={{ textAlign: 'center', marginX: 'auto' }}>{content}</Box>
      </TableCell>
    </TableRow>
  );
};

type PagingProps = {
  totalItemsCount: number;
  paging: IPagingData;
  itemsPerPageOptions?: number[];
  onChange: (paging: IPagingData) => void;
};


const TablePagination = ({ paging, onChange, totalItemsCount, itemsPerPageOptions }: PagingProps) => {
  return (
    <MuiPagination
      rowsPerPageOptions={itemsPerPageOptions}
      component="div"
      count={totalItemsCount}
      rowsPerPage={paging.itemsPerPage}
      page={totalItemsCount === 0 ? 0 : paging.page}
      showFirstButton
      showLastButton
      onPageChange={(event: any, newPage: number) =>
        onChange({
          page: newPage,
          itemsPerPage: paging.itemsPerPage,
        })
      }
      onRowsPerPageChange={(event: any) =>
        onChange({
          page: 0,
          itemsPerPage: Number(event.target.value),
        })
      }
    />
  );
};
